<template>
  <div class="outline-mobile-container">
    <!-- 操作按钮区域，固定在右下角 -->
    <div class="mobile-action-buttons">
      <!-- 查询按钮 -->
      <el-button class="mobile-icon-btn" @click="showFilterDialog">
        <i class="el-icon-search"></i>
      </el-button>
      
      <!-- 统计按钮 -->
      <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
        <i class="el-icon-data-analysis"></i>
      </el-button>
    </div>
    
    <!-- 标题栏 -->
    <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
      <h1 class="page-title">大纲掌握</h1>
    </div>
    
    <!-- 大纲卡片区域 -->
    <div class="outline-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
      <div v-if="outlineList.length === 0 && !loading" class="no-data">
        <div class="no-data-content">
          <i class="el-icon-document"></i>
          <span class="no-data-title">暂无大纲</span>
          <p class="no-data-tips">
            没有找到符合条件的大纲，请尝试调整筛选条件
          </p>
        </div>
      </div>
      
      <template v-else>
        <div 
          v-for="outline in outlineList" 
          :key="outline.id"
          class="outline-card"
          @click="handleCardClick(outline)"
        >
          <!-- 级别三角形 -->
          <div class="level-triangle">
            <div class="triangle-background"
                 :style="{ 
                   'border-color': `transparent ${getLevelColor(outline.level)} transparent transparent` 
                 }">
            </div>
            <span class="level-value">
              {{ outline.level }}
            </span>
          </div>
          
          <!-- 大纲内容 -->
          <div class="outline-content">
            <!-- 卡片名称改为三级大纲 -->
            <div class="outline-l1">{{ outline.outline_l3 }}</div>
            
            <!-- 二行合并显示：一级大纲 - 二级大纲 -->
            <div class="outline-l2">{{ [outline.outline_l1, outline.outline_l2].filter(Boolean).join(' - ') }}</div>
            
            <!-- 底部信息栏 -->
            <div class="outline-footer">
              <!-- 难度系数 -->
              <div class="difficulty-section">
                <span class="difficulty-label">难度：</span>
                <el-rate
                  :value="outline.cod"
                  disabled
                  :max="10"
                  :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
                  text-color="#ff9900"
                  score-template="{value}"
                  class="difficulty-rate">
                </el-rate>
              </div>
              
              <!-- 掌握程度 -->
              <div class="status-section" @click.stop="showStatusSelect(outline)">
                <span class="status-label">掌握程度：</span>
                <el-tag
                  :type="getStatusTagType(outline.status)"
                  :class="getStatusClass(outline.status)"
                  size="small"
                  class="status-tag">
                  {{ getStatusLabel(outline.status) }}
                </el-tag>
              </div>
            </div>
            
            <!-- 更新时间 -->
            <div class="update-time">
              <i class="el-icon-time"></i>
              {{ outline.update_time }}
            </div>
          </div>
        </div>
      </template>
      
      <!-- 加载更多 -->
      <div v-if="hasMore && !loading && outlineList.length > 0" class="load-more">
        <el-button type="text" @click="loadMore">加载更多</el-button>
      </div>
      <div v-if="loading && outlineList.length > 0" class="loading-more">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-if="!hasMore && outlineList.length > 0" class="no-more">
        <span>------已经到底啦------</span>
        <div class="copyright-info">
          <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
        </div>
      </div>
    </div>
    
    <!-- 自定义统计信息弹窗 -->
    <div class="dialog-container" v-show="statisticsDialogVisible">
      <div class="dialog-mask" @click="statisticsDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="statisticsDialogVisible = false">×</div>
        <div class="dialog-body statistics-dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">大纲掌握进度</h3>
            
            <!-- 级别统计卡片 -->
            <div class="level-statistics">
              <div 
                v-for="level in levelStats" 
                :key="level.name"
                class="level-stat-card"
                :class="[
                  { active: selectedLevel === level.name },
                  level.name === '入门级' ? 'entry-level' : '',
                  level.name === '提高级' ? 'advanced-level' : '',
                  level.name === 'NOI 级' ? 'noi-level' : ''
                ]"
                @click="selectLevel(level.name)">
                <div class="stat-value">{{ level.percentage.toFixed(2) }}%</div>
                <div class="stat-label">{{ level.label }}</div>
                <div class="progress-outer">
                  <div 
                    class="progress-inner" 
                    :style="{ 
                      width: level.percentage + '%', 
                      backgroundColor: level.color 
                    }">
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 详细进度表格 -->
            <div class="detail-progress">
              <h4 class="progress-title">{{ selectedLevelLabel }}进度详情</h4>
              <div class="progress-table">
                <div 
                  v-for="item in filteredDetailStatistics" 
                  :key="item.outline_l1"
                  class="progress-row">
                  <div class="progress-outline">{{ item.outline_l1 }}</div>
                  <div class="progress-bar-container">
                    <div class="progress-bar">
                      <div 
                        class="progress-inner"
                        :style="{ 
                          width: item.mastery_percent + '%',
                          backgroundColor: getProgressColor(selectedLevel)
                        }">
                      </div>
                    </div>
                    <span class="progress-value">{{ Number(item.mastery_percent).toFixed(2) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 自定义查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="filterForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="级别">
                <el-select v-model="filterForm.level" multiple collapse-tags placeholder="请选择级别" clearable style="width: 100%">
                  <el-option
                    v-for="item in levelOptions"
                    :key="item"
                    :label="item"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="一级大纲">
                <el-select v-model="filterForm.outline_l1" multiple collapse-tags filterable placeholder="请选择一级大纲" clearable style="width: 100%">
                  <el-option
                    v-for="item in outlineL1Options"
                    :key="item"
                    :label="item"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="二级大纲">
                <el-select v-model="filterForm.outline_l2" multiple collapse-tags filterable placeholder="请选择二级大纲" clearable style="width: 100%">
                  <el-option
                    v-for="item in outlineL2Options"
                    :key="item"
                    :label="item"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="三级大纲">
                <el-select 
                  v-model="filterForm.outline_l3" 
                  multiple 
                  collapse-tags 
                  filterable 
                  placeholder="请选择三级大纲" 
                  clearable 
                  style="width: 100%"
                  popper-class="outline-l3-dropdown">
                  <el-option
                    v-for="item in outlineL3Options"
                    :key="item"
                    :label="item"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="掌握程度">
                <el-select v-model="filterForm.status" multiple collapse-tags placeholder="请选择掌握程度" clearable style="width: 100%">
                  <el-option
                    v-for="item in statusOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 状态选择对话框 -->
    <div class="dialog-container" v-show="statusDialogVisible">
      <div class="dialog-mask" @click="statusDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 20vh;">
        <div class="dialog-close" @click="statusDialogVisible = false">×</div>
        <div class="dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">选择掌握程度</h3>
            <div class="status-options">
              <el-radio-group v-model="tempStatus" class="status-radio-group">
                <el-radio 
                  v-for="item in statusOptions" 
                  :key="item.value" 
                  :label="item.value" 
                  class="status-radio-item">
                  <div
                    :class="[
                      'custom-tag',
                      item.value === 4 ? 'mastered' : '',
                      item.value === 0 ? 'unknown' : '',
                      item.value === 1 ? 'aware' : '', 
                      item.value === 2 ? 'understand' : '',
                      item.value === 3 ? 'apply' : '',
                      item.value === 99 ? 'undefined' : ''
                    ]">
                    {{ item.label }}
                  </div>
                </el-radio>
              </el-radio-group>
            </div>
            <div class="dialog-footer">
              <el-button size="small" @click="statusDialogVisible = false">取 消</el-button>
              <el-button size="small" type="primary" @click="confirmStatusChange">确 定</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOutlineList, getOutlineStatistics, getOutlineDetailStatistics, updateOutlineStatus, getOutlineLevelOptions } from '@/api/outline'

export default {
  name: 'OutlineMobile',
  data() {
    return {
      loading: false,
      outlineList: [], // 当前页显示的数据
      currentPage: 1,
      pageSize: 20,
      total: 0,
      hasMore: true,
      sortProp: 'id',
      sortOrder: 'ascending',
      
      // 统计数据
      statistics: {
        basic: 0,     // 入门级掌握程度
        advanced: 0,  // 提高级掌握程度
        noi: 0        // NOI级掌握程度
      },
      
      // 详细统计数据
      detailStatistics: [],
      selectedLevel: '入门级', // 默认选中入门级
      
      // 弹窗控制
      statisticsDialogVisible: false,
      filterDialogVisible: false,
      statusDialogVisible: false,
      
      // 当前选中的大纲
      currentOutline: null,
      tempStatus: null,
      
      // 查询表单
      filterForm: {
        level: [],
        outline_l1: [],
        outline_l2: [],
        outline_l3: [],
        status: []
      },
      
      // 各选项列表
      levelOptions: [],
      outlineL1Options: [],
      outlineL2Options: [],
      outlineL3Options: [],
      statusOptions: [
        { value: 99, label: '未定义' },
        { value: 0, label: '未知' },
        { value: 1, label: '了解' },
        { value: 2, label: '理解' },
        { value: 3, label: '应用' },
        { value: 4, label: '掌握' }
      ],

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10 // 滚动阈值，避免微小滚动触发
    }
  },
  computed: {
    // 级别统计数据
    levelStats() {
      return [
        {
          name: '入门级',
          label: '入门级',
          percentage: this.statistics.basic || 0,
          color: '#67C23A'
        },
        {
          name: '提高级',
          label: '提高级',
          percentage: this.statistics.advanced || 0,
          color: '#409EFF'
        },
        {
          name: 'NOI 级',
          label: 'NOI 级',
          percentage: this.statistics.noi || 0,
          color: '#8E44AD'
        }
      ]
    },
    
    // 选中的级别标签
    selectedLevelLabel() {
      return this.selectedLevel
    },
    
    // 过滤后的详细统计数据
    filteredDetailStatistics() {
      return this.detailStatistics.filter(item => {
        // 根据选中的级别过滤数据
        if (this.selectedLevel === '入门级') {
          return item.level === '入门级'
        } else if (this.selectedLevel === '提高级') {
          return item.level === '提高级'
        } else if (this.selectedLevel === 'NOI 级') {
          return item.level === 'NOI 级'
        }
        return false
      })
    }
  },
  created() {
    this.fetchData()
    this.fetchStatistics()
    this.fetchLevelOptions()
    this.fetchAllOutlineOptions()
  },
  methods: {
    // 获取查询参数
    getQueryParams() {
      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize,
        sort_prop: this.sortProp,
        sort_order: this.sortOrder
      };
      
      // 添加查询参数
      if (this.filterForm.level && this.filterForm.level.length > 0) {
        params.level = this.filterForm.level;
      }
      
      if (this.filterForm.outline_l1 && this.filterForm.outline_l1.length > 0) {
        params.outline_l1 = this.filterForm.outline_l1;
      }
      
      if (this.filterForm.outline_l2 && this.filterForm.outline_l2.length > 0) {
        params.outline_l2 = this.filterForm.outline_l2;
      }
      
      if (this.filterForm.outline_l3 && this.filterForm.outline_l3.length > 0) {
        params.outline_l3 = this.filterForm.outline_l3;
      }
      
      if (this.filterForm.status && this.filterForm.status.length > 0) {
        params.status = this.filterForm.status;
      }
      
      return params;
    },
    
    // 获取大纲列表
    async fetchData(isLoadMore = false) {
      try {
        this.loading = true
        const params = this.getQueryParams()
        
        const res = await getOutlineList(params)
        if (res.code === 200) {
          if (isLoadMore) {
            this.outlineList = [...this.outlineList, ...res.data.items]
          } else {
            this.outlineList = res.data.items || []
          }
          this.total = res.data.total
          this.hasMore = this.outlineList.length < this.total
          
          // 如果是首次加载或没有选项数据，从返回的数据中提取
          if (this.outlineL1Options.length === 0 || this.outlineL2Options.length === 0 || this.outlineL3Options.length === 0) {
            this.extractOutlineOptions()
          }
        } else {
          this.$message.error(res.msg || '获取大纲列表失败')
        }
      } catch (error) {
        console.error('获取大纲列表失败:', error)
        this.$message.error('获取大纲列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取统计数据
    async fetchStatistics() {
      try {
        const response = await getOutlineStatistics()
        if (response && response.code === 200 && response.data) {
          this.statistics = {
            basic: response.data.basic || 0,
            advanced: response.data.advanced || 0,
            noi: response.data.noi || 0
          }
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    },
    
    // 获取详细统计数据
    async fetchDetailStatistics() {
      try {
        const response = await getOutlineDetailStatistics()
        if (response && response.code === 200 && response.data) {
          this.detailStatistics = response.data
        }
      } catch (error) {
        console.error('获取详细统计数据失败:', error)
      }
    },
    
    // 获取级别选项
    async fetchLevelOptions() {
      try {
        const response = await getOutlineLevelOptions()
        if (response && response.code === 200 && response.data) {
          this.levelOptions = response.data || []
        }
      } catch (error) {
        console.error('获取级别选项失败:', error)
      }
    },
    
    // 获取所有大纲选项
    async fetchAllOutlineOptions() {
      try {
        // 获取所有数据（尺寸很大，只用于提取选项）
        const params = {
          pageNum: 1,
          pageSize: 1000, // 获取大量数据以提取所有可能的选项
          sort_prop: 'id',
          sort_order: 'ascending'
        }
        
        const response = await getOutlineList(params)
        if (response && response.code === 200 && response.data && response.data.items) {
          this.processOutlineOptions(response.data.items)
        } else {
          // 降级：使用当前页数据
          this.extractOutlineOptions()
        }
      } catch (error) {
        console.error('获取所有大纲选项失败:', error)
        // 降级：使用当前页数据
        this.extractOutlineOptions()
      }
    },
    
    // 处理大纲选项数据
    processOutlineOptions(data) {
      // 提取一级大纲选项（去重）
      const l1Set = new Set(data.map(item => item.outline_l1))
      this.outlineL1Options = Array.from(l1Set).filter(Boolean).sort()
      
      // 提取二级大纲选项（去重）
      const l2Set = new Set(data.map(item => item.outline_l2))
      this.outlineL2Options = Array.from(l2Set).filter(Boolean).sort()
      
      // 提取三级大纲选项（去重）
      const l3Set = new Set(data.map(item => item.outline_l3))
      this.outlineL3Options = Array.from(l3Set).filter(Boolean).sort()
    },
    
    // 从当前数据中提取大纲选项（降级方案）
    extractOutlineOptions() {
      // 提取一级大纲选项（去重）
      const l1Set = new Set(this.outlineList.map(item => item.outline_l1))
      this.outlineL1Options = Array.from(l1Set).filter(Boolean).sort()
      
      // 提取二级大纲选项（去重）
      const l2Set = new Set(this.outlineList.map(item => item.outline_l2))
      this.outlineL2Options = Array.from(l2Set).filter(Boolean).sort()
      
      // 提取三级大纲选项（去重）
      const l3Set = new Set(this.outlineList.map(item => item.outline_l3))
      this.outlineL3Options = Array.from(l3Set).filter(Boolean).sort()
    },
    
    // 显示统计信息弹窗
    async showStatisticsDialog() {
      await this.fetchDetailStatistics()
      this.statisticsDialogVisible = true
    },
    
    // 显示查询弹窗
    showFilterDialog() {
      this.filterDialogVisible = true
    },
    
    // 选择级别
    selectLevel(level) {
      this.selectedLevel = level
    },
    
    // 处理卡片点击
    handleCardClick(outline) {
      // 这里可以添加卡片点击逻辑，比如显示详情
      console.log('大纲卡片被点击:', outline)
    },
    
    // 显示状态选择
    showStatusSelect(outline) {
      this.currentOutline = outline
      this.tempStatus = outline.status
      this.statusDialogVisible = true
    },
    
    // 确认状态修改
    async confirmStatusChange() {
      if (!this.currentOutline) return
      
      try {
        await updateOutlineStatus(this.currentOutline.id, this.tempStatus)
        this.$message.success('更新掌握程度成功')
        
        // 更新当前行状态
        this.currentOutline.status = this.tempStatus
        
        // 刷新统计数据
        this.fetchStatistics()
        
        this.statusDialogVisible = false
        this.currentOutline = null
        this.tempStatus = null
      } catch (error) {
        console.error('更新掌握程度失败:', error)
        this.$message.error('更新掌握程度失败')
      }
    },
    
    // 处理查询
    handleSearch() {
      this.currentPage = 1
      this.outlineList = []
      this.fetchData()
      this.fetchStatistics()
      this.filterDialogVisible = false
    },
    
    // 重置查询条件
    handleReset() {
      this.filterForm = {
        level: [],
        outline_l1: [],
        outline_l2: [],
        outline_l3: [],
        status: []
      }
    },
    
    // 加载更多
    loadMore() {
      if (!this.hasMore || this.loading) return
      
      this.currentPage++
      this.fetchData(true)
    },
    
    // 处理滚动事件，实现自动加载更多
    handleScroll(event) {
      const container = event.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 当滚动到距离底部100px时，加载更多内容（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
    },
    
    // 获取级别样式类
    getLevelClass(level) {
      const classMap = {
        '入门级': 'entry-level',
        '提高级': 'advanced-level',
        'NOI 级': 'noi-level'
      }
      return classMap[level] || 'default-level'
    },
    
    // 获取掌握程度标签类型
    getStatusTagType(status) {
      const typeMap = {
        99: 'info',    // 未定义
        0: 'danger',   // 未知
        1: 'warning',  // 了解
        2: 'success',  // 理解
        3: 'primary',  // 应用
        4: ''          // 掌握
      }
      return typeMap[status] || 'info'
    },
    
    // 获取掌握程度样式类
    getStatusClass(status) {
      const classMap = {
        99: 'undefined-tag',
        0: 'unknown-tag',
        1: 'aware-tag',
        2: 'understand-tag',
        3: 'apply-tag',
        4: 'mastered-tag'
      }
      return classMap[status] || 'default-tag'
    },
    
    // 获取掌握程度标签文字
    getStatusLabel(status) {
      const statusItem = this.statusOptions.find(item => item.value === status)
      return statusItem ? statusItem.label : '未知'
    },
    
    // 获取级别颜色
    getLevelColor(level) {
      const colorMap = {
        '入门级': 'rgba(103, 194, 58, 0.85)',  // 绿色
        '提高级': 'rgba(64, 158, 255, 0.85)',  // 蓝色
        'NOI 级': 'rgba(142, 68, 173, 0.85)'   // 紫色
      }
      return colorMap[level] || 'rgba(144, 147, 153, 0.85)' // 默认灰色
    },
    
    // 获取进度条颜色
    getProgressColor(level) {
      const colorMap = {
        '入门级': '#67C23A',
        '提高级': '#409EFF',
        'NOI 级': '#8E44AD'
      }
      return colorMap[level] || '#909399'
    }
  }
}
</script>

<style lang="scss" scoped>
.outline-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  position: relative;
  overflow: hidden;
}

/* 移动端操作按钮 - 与App.vue中全局菜单按钮样式保持一致 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  z-index: 2001;
  width: 40px;
  align-items: center;
  padding: 0 !important;
  margin: 0 !important;
  
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important;
  height: 40px !important;
  border-radius: 40px !important;
  background-color: rgba(255, 255, 255, 0.5) !important;
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important;
  transition: all 0.3s ease !important;
  position: relative !important;
  left: 0 !important;
  
  i {
    font-size: 20px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: inline-flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 20px !important;
    height: 20px !important;
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important;
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important;
    transform: scale(0.95) !important;
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 卡片容器 */
.outline-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  /* 大纲卡片 */
  .outline-card {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 14px;
    overflow: hidden;
    transition: all 0.3s ease;
    width: 100%;
    position: relative;
    border: 1px solid #ebeef5;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:last-child {
      margin-bottom: 20px;
    }
    
    /* 级别三角形 */
    .level-triangle {
      position: absolute;
      top: 0;
      right: 0;
      width: 60px;
      height: 60px;
      overflow: hidden;
      z-index: 1;
      pointer-events: none; /* 确保点击事件透过指示器 */
      
      .triangle-background {
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 60px 60px 0;
        opacity: 0.85; /* 稍微降低不透明度，使其更和谐 */
      }
      
      .level-value {
        position: absolute;
        /* 调整位置确保完全居中显示 */
        top: 15px;
        right: 8px;
        /* 使文本平行于斜边 */
        transform: rotate(45deg);
        color: white;
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        white-space: nowrap;
        width: 30px; /* 调整宽度确保文本居中 */
        line-height: 1;
        text-shadow: 0 1px 1px rgba(0,0,0,0.3);
      }
    }

    .outline-content {
      padding: 15px;
      padding-top: 10px;
      padding-right: 50px; /* 为右上角三角形留出更多空间 */
      
      .outline-l1 {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        line-height: 1.4;
        margin-bottom: 8px;
        word-break: break-word;
      }
      
      .outline-l2 {
        font-size: 14px;
        color: #606266;
        line-height: 1.3;
        margin-bottom: 6px;
        word-break: break-word;
      }
      
      .outline-l3 {
        font-size: 13px;
        color: #909399;
        line-height: 1.3;
        margin-bottom: 12px;
        word-break: break-word;
      }
      
      .outline-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        padding-top: 8px;
        border-top: 1px solid #f5f5f5;
        
        .difficulty-section {
          display: flex;
          align-items: center;
          flex: 1;
          
          .difficulty-label {
            font-size: 12px;
            color: #909399;
            margin-right: 4px;
          }
          
          .difficulty-rate {
            :deep(.el-rate__icon) {
              font-size: 12px;
              margin-right: 1px;
            }
          }
        }
        
        .status-section {
          display: flex;
          align-items: center;
          
          .status-label {
            font-size: 12px;
            color: #909399;
            margin-right: 4px;
          }
          
          .status-tag {
            cursor: pointer;
            
            &:hover {
              opacity: 0.8;
            }
          }
        }
      }
      
      .update-time {
        font-size: 11px;
        color: #c0c4cc;
        display: flex;
        align-items: center;
        
        i {
          margin-right: 4px;
          font-size: 12px;
        }
      }
    }
  }
  
  /* 暂无数据样式 */
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    
    .no-data-content {
      text-align: center;
      padding: 30px;
      
      i {
        font-size: 50px;
        color: #dcdfe6;
        margin-bottom: 20px;
      }
      
      .no-data-title {
        font-size: 18px;
        color: #606266;
        font-weight: 500;
        margin-bottom: 10px;
        display: block;
      }
      
      .no-data-tips {
        font-size: 14px;
        color: #909399;
      }
    }
  }
  
  /* 加载更多区域 */
  .load-more, .loading-more, .no-more {
    text-align: center;
    margin-top: 10px;
    padding: 10px 0;
    
    .el-button {
      font-size: 14px;
    }
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: #909399;
    
    i {
      font-size: 16px;
    }
  }
  
  .no-more {
    text-align: center;
    padding-bottom: 20px;
    
    span {
      color: #909399;
      font-size: 12px;
    }
    
    .copyright-info {
      text-align: center;
      width: 100%;
      margin: 15px auto 0;
      display: block;
      padding: 0 15px 15px;
      box-sizing: border-box;
      
      .mobile-copyright {
        text-align: center;
        width: 100%;
        color: #909399;
        font-size: 12px;
        line-height: 1.5;
      }
    }
  }
}

/* 级别统计卡片 */
.level-statistics {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  
  .level-stat-card {
    flex: 1;
    background-color: #f9fafc;
    border-radius: 8px;
    padding: 12px 8px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    
    &.active {
      &.entry-level {
        border-color: #67C23A;
        background-color: rgba(103, 194, 58, 0.1);
      }
      
      &.advanced-level {
        border-color: #409EFF;
        background-color: #ecf5ff;
      }
      
      &.noi-level {
        border-color: #8E44AD;
        background-color: rgba(142, 68, 173, 0.1);
      }
    }
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    .stat-value {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 4px;
    }
    
    .stat-label {
      font-size: 12px;
      color: #606266;
      margin-bottom: 8px;
    }
    
    .progress-outer {
      width: 100%;
      height: 4px;
      background-color: #ebeef5;
      border-radius: 2px;
      overflow: hidden;
      
      .progress-inner {
        height: 100%;
        transition: width 0.6s ease;
      }
    }
  }
}

/* 详细进度表格 */
.detail-progress {
  .progress-title {
    font-size: 14px;
    color: #303133;
    margin: 0 0 12px 0;
    font-weight: 600;
  }
  
  .progress-table {
    .progress-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      padding: 8px;
      background-color: #f9fafc;
      border-radius: 6px;
      
      .progress-outline {
        flex: 1;
        font-size: 13px;
        color: #606266;
        margin-right: 12px;
        word-break: break-word;
      }
      
      .progress-bar-container {
        display: flex;
        align-items: center;
        min-width: 120px;
        
        .progress-bar {
          flex: 1;
          height: 8px;
          background-color: #ebeef5;
          border-radius: 4px;
          overflow: hidden;
          margin-right: 8px;
          
          .progress-inner {
            height: 100%;
            transition: width 0.6s ease;
          }
        }
        
        .progress-value {
          font-size: 12px;
          font-weight: 600;
          color: #303133;
          min-width: 50px;
          text-align: right;
        }
      }
    }
  }
}

/* 自定义弹窗样式 */
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
}

.dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 2001;
}

.custom-dialog {
  position: absolute;
  z-index: 2002;
  width: 95%;
  max-height: 80vh;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.dialog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
}

.dialog-body {
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  &.statistics-dialog-body {
    padding: 15px;
  }
  
  &.filter-dialog-body {
    padding: 15px 15px 10px;
  }
}

.dialog-content {
  padding: 0;
  background-color: #ffffff !important;
  
  .dialog-custom-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 15px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    text-align: center;
    
    &.filter-title {
      font-size: 14px;
      margin: 0 0 10px 0;
      padding-bottom: 8px;
    }
  }
  
  .dialog-footer {
    text-align: right;
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
    
    &.filter-footer {
      margin-top: 10px;
      padding-top: 8px;
      
      .el-button {
        padding: 7px 15px;
      }
    }
  }
}

/* 查询表单样式 */
.filter-form {
  .el-form-item {
    margin-bottom: 10px;
    
    .el-form-item__label {
      padding-right: 5px;
      line-height: 32px;
    }
    
    .el-form-item__content {
      line-height: 32px;
    }
  }
  
  ::v-deep .el-input__inner,
  ::v-deep .el-select .el-input__inner {
    height: 32px;
    line-height: 32px;
  }
}

/* 状态选择样式 */
.status-options {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.status-radio-group {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important; /* 允许换行 */
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  gap: 8px 10px !important; /* 行列间距 */
}

.status-radio-item {
  margin: 0 !important;
  flex: 0 0 auto !important; /* 自适应内容宽度 */
}

.el-radio {
  display: flex !important;
  flex-direction: row !important; /* 左右排列 */
  align-items: center !important;
  justify-content: center !important;
  margin-right: 0 !important;
  padding: 4px 2px !important;
  border-radius: 6px !important;
  transition: all 0.3s !important;
  
  &:hover {
    background-color: #f5f7fa !important;
  }
  
  &.is-checked {
    background-color: #ecf5ff !important;
  }

  .el-radio__input {
    margin: 0 6px 0 0 !important; /* 与内容水平间距 */
  }

  .el-radio__label {
    padding-left: 0 !important;
    color: #606266 !important;
    font-size: 12px !important;
    margin-top: 0 !important;
    display: none !important; /* 使用自定义tag作为可视标签 */
  }

  .custom-tag {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: auto !important; /* 内容自适应，与页面标签一致 */
    min-width: 56px !important;
    height: 26px !important; /* 与页面小号标签高度一致 */
    padding: 0 10px !important; /* 与页面标签左右内边距一致 */
    border-radius: 4px !important; /* 与页面一致 */
    font-size: 12px !important; /* 与页面小字号一致 */
    font-weight: 500 !important;
    color: white !important;
    text-align: center !important;
    border: 1px solid transparent !important;

    &.mastered {
      background-color: #8E44AD !important;
      border-color: #8E44AD !important;
    }
    
    &.understand {
      background-color: #67C23A !important;
      border-color: #67C23A !important;
    }
    
    &.apply {
      background-color: #409EFF !important;
      border-color: #409EFF !important;
    }
    
    &.unknown {
      background-color: #F56C6C !important;
      border-color: #F56C6C !important;
    }
    
    &.aware {
      background-color: #E6A23C !important;
      border-color: #E6A23C !important;
    }
    
    &.undefined {
      background-color: #909399 !important;
      border-color: #909399 !important;
    }
  }
}

/* 小屏幕适配：窄屏时每行两个，保证可见性 */
@media (max-width: 360px) {
  .status-radio-group {
    gap: 8px 10px !important;
  }
  .status-radio-item {
    flex: 0 0 45% !important; /* 每行两个 */
    min-width: 120px !important;
  }
  .el-radio .custom-tag {
    height: 24px !important;
    font-size: 12px !important;
  }
}

/* 掌握程度标签样式 */
:deep(.el-tag) {
  &.mastered-tag {
    background-color: #8E44AD !important;
    border-color: #8E44AD !important;
    color: white !important;
  }
  
  &.understand-tag {
    background-color: #67C23A !important;
    border-color: #67C23A !important;
    color: white !important;
  }
  
  &.apply-tag {
    background-color: #409EFF !important;
    border-color: #409EFF !important;
    color: white !important;
  }
  
  &.unknown-tag {
    background-color: #F56C6C !important;
    border-color: #F56C6C !important;
    color: white !important;
  }
  
  &.aware-tag {
    background-color: #E6A23C !important;
    border-color: #E6A23C !important;
    color: white !important;
  }
  
  &.undefined-tag {
    background-color: #909399 !important;
    border-color: #909399 !important;
    color: white !important;
  }
}

/* 三级大纲下拉菜单样式 - 在默认基础上增加文字换行和自适应行高 */
:deep(.outline-l3-dropdown) {
  max-width: 300px !important;
  
  .el-select-dropdown__item {
    white-space: normal !important;
    word-break: break-word !important;
    height: auto !important;
  }
}
</style> 